// ProductEditor.js (Corrected for ReferenceError and Spin Warning)
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { EditOutlined } from '@ant-design/icons';
// Import Ant Design components
import { Steps, Button, message, Spin, Form, Input, InputNumber, Select, Table, ConfigProvider } from 'antd';
import esES from 'antd/locale/es_ES';
import { NODE_BACKEND_API_URL, NODE_BACKEND_PRD_URL, NODE_BACKEND_SCH_URL } from '../../config';
import { SyncOutlined } from '@ant-design/icons';
import '../../pages/producteditorpage.css';
import { allBrands as demoBrandsData } from '../../mocks/data/brands.demo';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const { Step } = Steps;
const { Option } = Select; // For AntD Select


// Base URL for your backend API
const API_BASE_URL = NODE_BACKEND_API_URL;
// Base URL for your search API
const SEARCH_API_URL = NODE_BACKEND_PRD_URL;

function ProductEditor() {
    // --- Existing State Variables ---
    const [mode, setMode] = useState('add'); // 'add' or 'edit' - still useful within form step
    const [brands, setBrands] = useState([]);
    const [selectedBrandId, setSelectedBrandId] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedProductForEdit, setSelectedProductForEdit] = useState(null);
    const [formData, setFormData] = useState({
        brandId: '',
        productCode: '',
        productDescription: '',
        ean: '',
        productCost: '',
        quantity: ''
    });
    const [loading, setLoading] = useState(true); // Initial brand loading
    const [error, setError] = useState(null); // Local error state for display within steps if needed
    const [isSearching, setIsSearching] = useState(false); // Search specific loading
    const [isFetchingProduct, setIsFetchingProduct] = useState(false); // Edit fetch loading
    const [isSubmitting, setIsSubmitting] = useState(false); // Form submission loading
    const [searchAttempted, setSearchAttempted] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null); // Local success state for display if needed
    const [isCheckingCode, setIsCheckingCode] = useState(false); // Code uniqueness check loading

    // --- New State for Steps and Branching ---
    const [currentStep, setCurrentStep] = useState(0); // Start at the first step (index 0)
    const [actionType, setActionType] = useState(null); // 'modify' or 'add' - set in Step 2
    const [uniquenessCheckResult, setUniquenessCheckResult] = useState(null); // null, 'unique', 'exists'
    const [submissionResult, setSubmissionResult] = useState(null); // null, { success: true/false, message: string, data: productObject }
    const [isGeneratingEAN, setIsGeneratingEAN] = useState(false);

    // Define the steps configuration
    const steps = [
        { title: 'Marca' },
        { title: 'Acción' }, // Modify or Add
        { title: actionType === 'modify' ? 'Buscar Producto' : 'Validar Código' }, // Dynamic title based on path
        { title: 'Detalles' }, // Fill/Edit Form
        { title: 'Resultado' }, // Success/Failure Message & Data
    ];

    // Ant Design Form instance for validation
    const [form] = Form.useForm();

    // --- Effects ---

    // Effect to fetch the list of brands on component mount
    useEffect(() => {
        if (isDemoMode) {
            const brandOptions = demoBrandsData.brands
                .filter(b => b.brand !== 'DUTTO')
                .map(b => ({
                    value: String(b.id),
                    label: b.brand,
                }));
            setBrands(brandOptions);
            setLoading(false);
            return;
        }
        axios.get(`${API_BASE_URL}/getallbrands`)
            .then(res => {
                const brandOptions = res.data.map(brand => ({
                    value: String(brand.BrandId), // Ensure value is string for select
                    label: brand.BrandName
                }));
                setBrands(brandOptions);
                setLoading(false);
            })
            .catch(er => {
                console.error("Error fetching brands:", er);
                message.error("Error al cargar la lista de marcas.");
                setError("Error al cargar la lista de marcas."); // Keep local error state
                setLoading(false);
            });
    }, []);

    // Effect to fetch product details when a product is selected for editing (Step 3 - Modify Path)
    useEffect(() => {
        if (currentStep === 3 && actionType === 'modify' && selectedProductForEdit) {
            setIsFetchingProduct(true);
            setError(null);
            // setSuccessMessage(null); // AntD message handles success
            form.resetFields(); // Reset form before filling
            axios.get(`${API_BASE_URL}/getproductbyid/${selectedProductForEdit.ProductId}`)
                .then(res => {
                    const productDetails = res.data;
                    const formattedData = {
                        brandId: String(productDetails.BrandId) || '',
                        productCode: productDetails.ProductCode || '',
                        productDescription: productDetails.ProductDescription || '',
                        ean: productDetails.EAN || '',
                        productCost: productDetails.ProductCost != null ? String(productDetails.ProductCost) : '',
                        quantity: productDetails.Quantity != null ? String(productDetails.Quantity) : ''
                    };
                    setFormData(formattedData); // Update local formData state
                    form.setFieldsValue(formattedData); // Set AntD form fields
                    setIsFetchingProduct(false);
                })
                .catch(er => {
                    console.error(`Error fetching product details for ID ${selectedProductForEdit.ProductId}:`, er);
                    message.error("Error al cargar los detalles del producto para editar.");
                    setError("Error al cargar los detalles del producto para editar."); // Keep local error state
                    setIsFetchingProduct(false);
                    // Handle error: Go back to search step
                    setCurrentStep(2); // Go back to Search step (index 2)
                    setActionType('modify'); // Ensure we are on the modify path
                    setSelectedProductForEdit(null);
                    resetForm(); // Clear form data
                    form.resetFields(); // Reset AntD form fields
                });
        }
        // If we are in Step 3 (Details) in 'add' path, ensure brandId is set in form data
        if (currentStep === 3 && actionType === 'add') {
            const currentBrandId = selectedBrandId; // Use current selectedBrandId
            form.setFieldsValue({ brandId: currentBrandId });
            setFormData(prev => ({ ...prev, brandId: currentBrandId })); // Keep local state synced
        }
    }, [currentStep, actionType, selectedProductForEdit, selectedBrandId]); // Added form as dependency


    // Effect to clear local error/success messages (AntD message handles its own)
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error, successMessage]);

    // Effect to reset uniqueness check result when product code or brand changes
    useEffect(() => {
        if (currentStep === 2 && actionType === 'add') {
            setUniquenessCheckResult(null); // Reset result when entering/staying on this step
        }
    }, [currentStep, actionType, formData.productCode, selectedBrandId]);


    // --- Handlers ---

    // Define resetForm before handlers that use it
    const resetForm = () => {
        setFormData({
            brandId: selectedBrandId, // Keep selected brand for new product context
            productCode: '',
            productDescription: '',
            ean: '',
            productCost: '',
            quantity: ''
        });
        setSelectedProductForEdit(null); // Ensure no product is selected when form is reset
        // Error and success messages are handled by AntD message or cleared by effect
        // setSuccessMessage(null); // Clear local success message too
        // setError(null); // Clear local error message too
    };


    // Handles brand selection (Step 1)
    const handleBrandChange = (value) => { // AntD Select passes value directly
        setSelectedBrandId(value);
        // Reset state related to subsequent steps when brand changes
        setActionType(null);
        setSearchQuery('');
        setSearchResults([]);
        setSearchAttempted(false);
        setSelectedProductForEdit(null);
        resetForm(); // <-- Defined BEFORE use
        form.resetFields(); // Reset AntD form fields (clears inputs visually)
        setUniquenessCheckResult(null);
        setSubmissionResult(null);
        setError(null);
        setSuccessMessage(null); // Clear messages when brand changes
        // Stay on Step 1, Next button will be enabled
    };

    // Handles search input change (Step 2 - Modify Path)
    const handleSearchInputChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        setSearchResults([]);
        setSearchAttempted(false);
        // setError(null); // AntD message handles errors
    };

    // Handles search button click (Step 2 - Modify Path)
    const handleSearchButtonClick = () => {
        if (!selectedBrandId) {
            message.warning("Error interno: No hay marca seleccionada."); // Should not happen if flow is followed
            return;
        }
        if (searchQuery.trim() === '') {
            message.warning("Por favor, escriba un término de búsqueda.");
            return;
        }

        setIsSearching(true);
        // setError(null); // AntD message handles errors
        setSearchAttempted(true);
        setSearchResults([]);

        axios.get(`${SEARCH_API_URL}/search?search=${encodeURIComponent(searchQuery)}&brandId=${encodeURIComponent(selectedBrandId)}`)
            .then(res => {
                setSearchResults(res.data);
                setIsSearching(false);
                if (res.data.length === 0) {
                    message.info("No se encontraron productos que coincidan.");
                }
            })
            .catch(er => {
                console.error("Error during product search:", er);
                message.error("Error al buscar productos.");
                setError("Error al buscar productos."); // Keep local error state
                setSearchResults([]);
                setIsSearching(false);
            });
    };

    // Called when a product is selected from the search results (Step 2 - Modify Path)
    const handleSelectProduct = (product) => {
        setSelectedProductForEdit(product);
        setMode('edit'); // Switch mode to edit
        // Proceed to the next step (Product Details)
        setCurrentStep(3); // Go to Step 3 (Details)
        // Search query and results will be cleared when leaving Step 2 via prevStep if needed
        setError(null); // Clear local error
        setSuccessMessage(null); // Clear local success
    };

    // Handles "Modify Existing Product" button click (Step 1)
    const handleChooseModify = () => {
        setActionType('modify');
        setCurrentStep(2); // Move to Step 2 (Search/Validate)
        setSearchQuery(''); // Clear previous search query
        setSearchResults([]); // Clear previous search results
        setSearchAttempted(false);
        setError(null); // Clear local error
        setSuccessMessage(null); // Clear local success
    };

    // Handles "Add New Product" button click (Step 1)
    const handleChooseAdd = () => {
        setActionType('add');
        setMode('add'); // Ensure mode is add
        setSelectedProductForEdit(null); // Clear any selected product
        resetForm(); // Reset form for a new product
        form.resetFields(); // Reset AntD form fields
        setCurrentStep(2); // Move to Step 2 (Search/Validate)
        setError(null); // Clear local error
        setSuccessMessage(null); // Clear local success
    };

    // Handles input change for the Product Code field (Step 2 - Add Path)
    const handleProductCodeChange = (e) => {
        const code = e.target.value;
        setFormData(prev => ({ ...prev, productCode: code }));
        form.setFieldsValue({ productCode: code }); // Sync with AntD form
        setUniquenessCheckResult(null); // Reset check result on input change
        setError(null); // Clear local error
        setSuccessMessage(null); // Clear local success
    };

    // Handles the "Check Product Code" button click (Step 2 - Add Path)
    const handleCheckProductCode = async () => {
        if (!selectedBrandId) {
            message.warning("Error interno: No hay marca seleccionada."); // Should not happen
            return;
        }
        if (!formData.productCode.trim()) {
            message.warning("Por favor, ingrese un código de producto.");
            return;
        }

        setIsCheckingCode(true);
        setUniquenessCheckResult(null); // Reset result before checking
        setError(null); // Clear local error
        setSuccessMessage(null); // Clear local success

        try {
            const checkResponse = await axios.get(`${API_BASE_URL}/getcheckproductcode?brandId=${encodeURIComponent(selectedBrandId)}&productCode=${encodeURIComponent(formData.productCode)}`);
            setIsCheckingCode(false);
            if (checkResponse.data.exists) {
                setUniquenessCheckResult('exists');
                message.warning(`Ya existe un producto con el código "${formData.productCode}" para la marca seleccionada.`);
                setError(`Ya existe un producto con el código "${formData.productCode}" para la marca seleccionada.`); // Keep local error
            } else {
                setUniquenessCheckResult('unique');
                // If unique, automatically proceed to the next step (Details)
                setCurrentStep(3); // Move to Step 3 (Details)
                setMode('add'); // Ensure mode is add
                // formData.brandId is already set via selectedBrandId and effect
                setError(null); // Clear local error
                setSuccessMessage("Código de producto disponible. Puede continuar."); // Keep local success
            }
        } catch (er) {
            console.error("Error checking product code:", er);
            message.error("Error al verificar el código de producto.");
            setError("Error al verificar el código de producto."); // Keep local error state
            setIsCheckingCode(false);
            setUniquenessCheckResult(null); // Reset result on error
        }
    };

    // Handles "Modify Existing" button click after uniqueness check shows 'exists' (Step 2 - Add Path)
    const handleGoToModifyExisting = async () => {
        if (!selectedBrandId || !formData.productCode.trim()) {
            message.warning("Error interno: Marca o código de producto inválido.");
            return;
        }
        // Attempt to fetch the product details immediately
        setIsFetchingProduct(true); // Use fetching product loader
        setError(null);
        setSuccessMessage(null);

        try {
            const productDetailsResponse = await axios.get(`${API_BASE_URL}/getproductbycode?brandId=${encodeURIComponent(selectedBrandId)}&productCode=${encodeURIComponent(formData.productCode)}`);
            const productDetails = productDetailsResponse.data; // Assuming the API returns the product details
            if (productDetails) {
                setSelectedProductForEdit(productDetails); // Set the found product
                setMode('edit'); // Set mode to edit
                setActionType('modify'); // Switch to modify path
                setCurrentStep(3); // Go directly to Step 3 (Details)
                // useEffect for Step 3 will fetch full details, but setting selectedProductForEdit primes it.
                // Alternatively, populate formData here directly if the API returns all necessary fields.
                const formattedData = {
                    brandId: String(productDetails.BrandId) || '',
                    productCode: productDetails.ProductCode || '',
                    productDescription: productDetails.ProductDescription || '',
                    ean: productDetails.EAN || '',
                    productCost: productDetails.ProductCost != null ? String(productDetails.ProductCost) : '',
                    quantity: productDetails.Quantity != null ? String(productDetails.Quantity) : ''
                };
                setFormData(formattedData); // Update local state
                form.setFieldsValue(formattedData); // Update AntD form fields
                message.info(`Cargando detalles para editar: ${productDetails.ProductCode}`); // Inform user
                setError(null); // Clear local error
                setSuccessMessage(null); // Clear local success

            } else {
                message.error("Error: Producto no encontrado con el código proporcionado.");
                setError("Error: Producto no encontrado con el código proporcionado.");
                // Stay on Step 2 Add Path, reset check result?
                setUniquenessCheckResult(null);
            }
        } catch (er) {
            console.error("Error fetching product by code:", er);
            message.error("Error al intentar cargar el producto para editar.");
            setError("Error al intentar cargar el producto para editar."); // Keep local error
            // Stay on Step 2 Add Path, reset check result?
            setUniquenessCheckResult(null);
        } finally {
            setIsFetchingProduct(false);
        }
        // Reset check result when leaving this state, regardless of outcome
        setUniquenessCheckResult(null);

    };

    // Handles "Try Another ProductCode" button click after uniqueness check shows 'exists' (Step 2 - Add Path)
    const handleTryAnotherCode = () => {
        setFormData(prev => ({ ...prev, productCode: '' })); // Clear product code field
        form.setFieldsValue({ productCode: '' }); // Sync with AntD form
        setUniquenessCheckResult(null); // Clear uniqueness check state
        setError(null); // Clear local error
        setSuccessMessage(null); // Clear local success
        // Stay on Step 2 (Add Path)
    };

    const handleGenerateEAN = async () => {
        const brandId = form.getFieldValue('brandId');
        if (!brandId) {
            message.warning('Por favor, seleccione una marca primero.');
            return;
        }

        // Check if the token exists, as a fallback to the interceptor
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
            message.error('No se encontró el token de autenticación. Por favor, inicie sesión de nuevo.');
            return;
        }

        setIsGeneratingEAN(true);
        try {
            // Use axios.post instead of fetch
            const response = await axios.post(`${NODE_BACKEND_API_URL}/generateEAN`, { brandId }, {
                headers: {
                    // The axios interceptor should handle this automatically,
                    // but you can be explicit here for clarity if you want.
                    // However, letting the interceptor handle it is the point of using it.
                    // It's cleaner to remove the Authorization header from here
                    // and rely on the interceptor, as long as your URL matches the pattern.
                    'Content-Type': 'application/json'
                }
            });

            // ... rest of the code
            const data = response.data; // Axios wraps the response in a 'data' property
            form.setFieldsValue({ ean: data.ean });
            setFormData(prev => ({ ...prev, ean: data.ean }));
            message.success('EAN generado con éxito.');
        } catch (err) {
            // Axios errors are typically in err.response.data
            console.error('Error generating EAN:', err.response?.data?.msg || err.message);
            message.error(err.response?.data?.msg || 'Error al generar EAN. Inténtelo de nuevo.');
        } finally {
            setIsGeneratingEAN(false);
        }
    };


    // Handles AntD form value changes (Alternative to handleFormInputChange if using Form.Item 'name')
    const handleAntdFormChange = (changedValues, allValues) => {
        // Update local state, but be careful not to override brandId if using AntD Select for brand
        // In this flow, brandId is handled by selectedBrandId and set when entering step 3.
        // Let's update only fields other than brandId if it's a new product
        if (actionType === 'add') {
            setFormData(prev => ({ ...prev, ...changedValues }));
        } else { // Edit mode, update all fields including potentially brandId if allowed (though current design keeps brand fixed)
            setFormData(allValues); // Sync all fields
        }
    };


    // --- Step Navigation Handlers ---

    const nextStep = () => {
        // Validation before moving to the next step
        if (currentStep === 0) {
            // Step 0: Select Brand
            if (!selectedBrandId) {
                message.warning('Por favor, seleccione una marca antes de continuar.');
                return;
            }
            // Can move to Step 1 once a brand is selected
            setCurrentStep(currentStep + 1);
            setError(null); // Clear local error on step change
            setSuccessMessage(null); // Clear local success on step change

        } else if (currentStep === 1) {
            // Step 1: Choose Action
            // The user needs to click either "Modify Existing" or "Add New"
            // which will set actionType and navigate to Step 2.
            // No generic "Next" button needed here.

        } else if (currentStep === 2) {
            // Step 2: Search / Validate Code
            // The user needs to either select a product (modify path) or
            // successfully validate the code (add path) to proceed to Step 3.
            // This transition is action-driven, not via a generic "Next" button.
            // This nextStep handler is actually not needed if transitions are only by actions within step 2.
            // Keeping it here as a safeguard but disabling the button in render.

        } else if (currentStep === 3) {
            // Step 3: Product Details & Submit
            // The "Submit" button is on this step and calls handleSubmit directly.
            // The "Next" button is not needed here.

        } else if (currentStep === 4) {
            // Step 4: Result
            // No "Next" button needed here, only reset/retry options
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            // Reset state specific to the step being left if necessary
            if (currentStep === 1) {
                // Leaving Step 1 (Choose Action) to go back to Step 0 (Select Brand)
                setActionType(null); // Clear action type
                setSelectedProductForEdit(null); // Clear selected product
                resetForm(); // Clear form data
                form.resetFields(); // Reset AntD form fields
                setSearchQuery('');
                setSearchResults([]);
                setSearchAttempted(false);
                setUniquenessCheckResult(null);
                setSubmissionResult(null);
                setError(null); // Clear local error
                setSuccessMessage(null); // Clear local success

            } else if (currentStep === 2) {
                // Leaving Step 2 (Search/Validate) to go back to Step 1 (Choose Action)
                // Keep selectedBrandId as user chose it in Step 0
                setActionType(null); // Clear action type to go back to choose action step
                setSearchQuery('');
                setSearchResults([]);
                setSearchAttempted(false);
                setSelectedProductForEdit(null);
                setUniquenessCheckResult(null); // Clear uniqueness result
                resetForm(); // Clear form data just in case
                form.resetFields(); // Clear form fields
                setError(null); // Clear local error
                setSuccessMessage(null); // Clear local success


            } else if (currentStep === 3) {
                // Leaving Step 3 (Details) to go back to Step 2 (Search/Validate)
                resetForm(); // Clear form data
                form.resetFields(); // Reset AntD form fields
                setSelectedProductForEdit(null); // Ensure no product is selected
                // Keep actionType to return to the correct path in Step 2
                setUniquenessCheckResult(null); // Clear uniqueness check result when leaving details
                setError(null); // Clear local error
                setSuccessMessage(null); // Clear local success


            } else if (currentStep === 4) {
                // Leaving Step 4 (Result) to go back to Step 3 (Details) for Retry
                setSubmissionResult(null); // Clear previous result
                // Keep formData and selectedProductForEdit to allow retrying submission
                // Also keep actionType
                setError(null); // Clear local error
                setSuccessMessage(null); // Clear local success
            }
            setCurrentStep(currentStep - 1);
        }
    };

    // Handles "Start New Process" button click (Step 4)
    const handleStartNewProcess = () => {
        setCurrentStep(0); // Go back to the first step
        setActionType(null); // Clear action type
        setSelectedBrandId(''); // Clear selected brand
        setSearchQuery('');
        setSearchResults([]);
        setSearchAttempted(false);
        setSelectedProductForEdit(null);
        resetForm(); // Clear form data
        form.resetFields(); // Reset AntD form fields
        setUniquenessCheckResult(null);
        setSubmissionResult(null);
        setError(null); // Clear local error
        setSuccessMessage(null); // Clear local success
    };

    // Handles "Retry" button click (Step 4)
    const handleRetry = () => {
        setSubmissionResult(null); // Clear previous result message
        setCurrentStep(3); // Go back to Step 3 (Details) to allow editing and resubmitting
        setError(null); // Clear local error
        setSuccessMessage(null); // Clear local success
    };


    // Handle form submission (called by the submit button on step 4)
    const handleSubmit = async () => { // Use async function for AntD Form onFinish
        // Trigger AntD form validation first
        try {
            const values = await form.validateFields();
            // If validation succeeds, update local state from AntD form values
            // setFormData(values); // Already synced by handleAntdFormChange
            // Proceed with submission logic
            setIsSubmitting(true);
            setError(null); // AntD message will handle errors
            setSubmissionResult(null); // Clear previous submission result

            const productDataToSend = {
                ...values, // Use validated values from AntD form
                brandId: selectedBrandId, // Ensure correct brandId is sent
                // Ensure numbers are numbers, although AntD InputNumber might handle this
                productCost: parseFloat(values.productCost),
                quantity: parseInt(values.quantity, 10)
            };

            // If editing, add the ProductId
            if (mode === 'edit' && selectedProductForEdit) {
                productDataToSend.ProductId = selectedProductForEdit.ProductId;
            }

            console.log(`Attempting to ${mode === 'add' ? 'add' : 'update'} product:`, productDataToSend);

            let response;
            if (mode === 'add') {
                // Uniqueness check is done in Step 2 now, so proceed directly to add
                response = await axios.post(`${API_BASE_URL}/postaddproduct`, productDataToSend);
            } else if (mode === 'edit' && selectedProductForEdit) {
                // Assuming update endpoint uses ProductId in the URL
                response = await axios.post(`${API_BASE_URL}/postupdateproduct/${selectedProductForEdit.ProductId}`, productDataToSend);
            } else {
                // Should not happen if flow is correct
                throw new Error("Estado interno inválido para la sumisión.");
            }

            console.log("Submission Response:", response.data);

            // Handle successful submission
            setSubmissionResult({
                success: true,
                message: mode === 'add'
                    ? `Producto agregado exitosamente! ID: ${response.data.productId || 'N/A'}`
                    : "Producto actualizado exitosamente!",
                data: response.data // Backend should ideally return the saved product data
            });
            message.success(mode === 'add' ? "Producto agregado exitosamente!" : "Producto actualizado exitosamente!");

            // Move to the Result step
            setCurrentStep(4); // Go to Step 4 (Result)
            setError(null); // Clear local error
            setSuccessMessage(mode === 'add' ? "Producto agregado exitosamente!" : "Producto actualizado exitosamente!"); // Set local success

        } catch (er) {
            // This catch block handles both AntD form validation errors and API errors
            setIsSubmitting(false); // Stop loading here if error occurs
            setIsCheckingCode(false); // Ensure checking loading stops

            if (er.errorFields) {
                // AntD Form validation failed
                console.log('AntD Form validation failed:', er.errorFields);
                message.warning('Por favor, complete los campos requeridos correctamente.');
                setError("Errores en el formulario. Por favor, revise los campos."); // Update local error state
                setSuccessMessage(null); // Clear local success
                // Stay on Step 3
            } else {
                // API or other unexpected error
                console.error(`Error ${mode === 'add' ? 'agregando' : 'actualizando'} producto:`, er);
                let errorMessage = `Error al ${mode === 'add' ? 'agregar' : 'actualizar'} producto.`;

                if (er.response) {
                    if (er.response.status === 400) {
                        errorMessage = `Error de validación en servidor: ${er.response.data.msg || er.response.statusText}`;
                    } else if (er.response.status === 409) {
                        errorMessage = `Conflicto: ${er.response.data.msg || er.response.statusText}`;
                    } else if (er.response.status === 404 && mode === 'edit') {
                        errorMessage = "Error: Producto no encontrado para actualizar.";
                    } else {
                        errorMessage = `Error del servidor: ${er.response.data.msg || er.response.statusText || er.message}`;
                    }
                } else if (er.request) {
                    errorMessage = "Error de red: No se pudo conectar con el servidor.";
                } else {
                    errorMessage = `Error inesperado: ${er.message}`;
                }

                setSubmissionResult({
                    success: false,
                    message: errorMessage,
                    data: formData // Send current form data back for review on error
                });
                message.error(errorMessage);
                setError(errorMessage); // Update local error state
                setSuccessMessage(null); // Clear local success

                // Move to the Result step to show the error message
                setCurrentStep(4); // Go to Step 4 (Result)
            }
        } finally {
            setIsSubmitting(false); // Ensure submitting state is false
            setIsCheckingCode(false); // Ensure checking state is false
            // isFetchingProduct might be true if there was an error during fetch in Step 3 and then submit fails
            // but ideally isFetchingProduct is false by the time submit is called.
            // Let's ensure it's false here just in case.
            setIsFetchingProduct(false);
        }
    };

    // Helper to render product data in a table
    const renderProductTable = (data) => {
        if (!data) return null;

        // Dynamically create columns based on keys in the data object
        const columns = Object.keys(data).map(key => ({
            title: key, // Use the key as the column title
            dataIndex: key,
            key: key,
            // Optional: Render boolean values nicely
            render: (text) => typeof text === 'boolean' ? (text ? 'Sí' : 'No') : text,
        }));


        const dataSource = [{
            key: data.ProductId || 'new', // Use product ID or 'new' for key
            ...data // Spread the product data
        }];

        return (
            <Table
                columns={columns}
                dataSource={dataSource}
                pagination={false} // No pagination for a single row
                size="small" // Compact table size
                style={{ margin: '20px 0' }}
                bordered // Add borders for clarity
            />
        );
    };


    // --- Render Logic ---

    // Initial loading state for brands
    if (loading) {
        return (
            <div className="editor-page-container">
                <div className="editor-content-container" style={{ textAlign: 'center', padding: '50px' }}>
                    <Spin tip="Cargando lista de marcas..." size="large" />
                </div>
            </div>
        );
    }

    // Initial error state if brands failed to load and they are essential
    if (error && brands.length === 0 && currentStep === 0) {
        // Show this only on the first step if brands didn't load
        return (
            <div className="editor-page-container">
                <div className="editor-content-container" style={{ textAlign: 'center', padding: '50px', color: '#ff4d4f' }}>
                    <p>{error}</p>
                    <p style={{ color: '#666', marginTop: '0.5rem' }}>No se pudieron cargar las marcas necesarias para el editor. Intente recargar.</p>
                </div>
            </div>
        );
    }

    // Main render function starts here
    return (
        <ConfigProvider locale={esES}>
        <div className="editor-page-container">
            {/* Header */}
            <div className="editor-header">
                <h1>
                    <EditOutlined />
                    Editor de Productos
                </h1>
            </div>

            {/* Steps */}
            <div className="editor-steps-container">
                <Steps current={currentStep} className="app-steps">
                    {steps.map((item, index) => (
                        <Step key={index} title={item.title} description={item.description} />
                    ))}
                </Steps>
            </div>

            {/* Content */}
            <div className="editor-content-container">

                {/* --- Step Content Area --- */}
                <div className="steps-content">
                    {/* Step 1: Select Brand (AntD Index 0) */}
                    {currentStep === 0 && (
                        <div>
                            <h3 style={{ fontSize: '1.5rem', marginBottom: '0.75rem', paddingBottom: '0.5rem' }}>
                                Seleccionar Marca
                            </h3>
                            {/* Use AntD Form for brand selection validation */}
                            {/* Note: This form is separate from the main product details form */}
                            <Form layout="vertical">
                                <Form.Item
                                    label="Marca"
                                    name="brandSelect" // Give it a name for form.setFieldsValue
                                    rules={[{ required: true, message: 'Por favor, seleccione una marca!' }]}
                                    initialValue={selectedBrandId} // Set initial value from state
                                // Add onChange to trigger form item value change and update state
                                // Although AntD Select onChange gives the value directly,
                                // using Form.Item's name and initialValue helps AntD manage its state.
                                // The direct handleBrandChange updates our component state.
                                >
                                    {/* Use AntD Select */}
                                    <Select
                                        placeholder="-- Seleccionar Marca --"
                                        onChange={handleBrandChange} // Use onChange directly with value
                                        disabled={brands.length === 0 || loading} // Disable while loading brands
                                        style={{ width: '100%' }}
                                        showSearch // Add search functionality to select
                                        optionFilterProp="children" // Search based on option label
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                    >
                                        {brands.map(brand => (
                                            <Option key={brand.value} value={brand.value}>
                                                {brand.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Form>
                            {/* Local error for brand loading can be shown here */}
                            {/* {error && brands.length > 0 && <p style={{ fontSize: '0.875rem', color: 'red', marginTop: '0.5rem' }}>{error}</p>} */}
                        </div>
                    )}

                    {/* Step 2: Choose Action (AntD Index 1) */}
                    {currentStep === 1 && (
                        <div style={{ textAlign: 'center', padding: '30px' }}>
                            <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>
                                ¿Qué desea hacer?
                            </h3>
                            {/* Display selected brand */}
                            <p style={{ marginBottom: '2rem' }}>
                                Marca Seleccionada: <strong>{brands.find(b => String(b.value) === String(selectedBrandId))?.label || 'N/A'}</strong>
                            </p>
                            <Button
                                type="primary"
                                size="large"
                                style={{ margin: '0 10px' }}
                                onClick={handleChooseModify}
                            >
                                Modificar Producto Existente
                            </Button>
                            <Button
                                type="default"
                                size="large"
                                style={{ margin: '0 10px' }}
                                onClick={handleChooseAdd}
                            >
                                Agregar Nuevo Producto
                            </Button>
                        </div>
                    )}

                    {/* Step 3: Search / Validate Code (AntD Index 2) - Conditional based on actionType */}
                    {currentStep === 2 && (
                        <>
                            {actionType === 'modify' && (
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', marginBottom: '0.75rem', paddingBottom: '0.5rem' }}>
                                        Buscar Producto para Editar
                                    </h3>
                                    {/* Display selected brand */}
                                    <p style={{ marginBottom: '1rem' }}>
                                        Marca Seleccionada: <strong>{brands.find(b => String(b.value) === String(selectedBrandId))?.label || 'N/A'}</strong>
                                    </p>

                                    {/* Search Input and Button */}
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                                        <label htmlFor="productSearch" className="editor-label" style={{ marginRight: '0.5rem', minWidth: '100px' }}>Buscar:</label>
                                        <div style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                            <Input
                                                id="productSearch"
                                                placeholder="Código, Descripción o EAN"
                                                value={searchQuery}
                                                onChange={handleSearchInputChange}
                                                disabled={isSearching}
                                                style={{ flexGrow: 1, marginRight: '0.5rem' }}
                                            />
                                            <Button
                                                type="primary"
                                                onClick={handleSearchButtonClick}
                                                loading={isSearching}
                                                disabled={!searchQuery.trim() || isSearching}
                                            >
                                                Buscar
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Search Results */}
                                    {searchAttempted && !isSearching && searchResults.length > 0 && (
                                        <div style={{ border: '1px solid #ccc', borderRadius: '0.25rem', maxHeight: '10rem', overflowY: 'auto', marginTop: '0.5rem' }}>
                                            <ul>
                                                {searchResults.map(product => (
                                                    <li
                                                        key={product.ProductId}
                                                        style={{
                                                            padding: '0.5rem 0.75rem',
                                                            borderBottom: '1px solid #eee',
                                                            cursor: 'pointer',
                                                            fontSize: '0.875rem',
                                                            backgroundColor: selectedProductForEdit?.ProductId === product.ProductId ? '#e0f2f7' : 'transparent',
                                                            listStyle: 'none', // Remove default list style
                                                            margin: 0,
                                                            // Add hover effect
                                                            transition: 'background-color 0.2s ease',
                                                        }}
                                                        onMouseOver={e => { e.currentTarget.style.backgroundColor = '#f0f0f0', e.currentTarget.style.color = 'black' }} // Highlight on hover
                                                        onMouseOut={e => e.currentTarget.style.backgroundColor = selectedProductForEdit?.ProductId === product.ProductId ? '#e0f2f7' : 'transparent'} // Remove highlight
                                                        onClick={() => handleSelectProduct(product)}
                                                    >
                                                        <strong>{product.ProductCode}</strong> - {product.ProductDescription} ({product.BrandName})
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Message if no search results */}
                                    {searchAttempted && !isSearching && searchResults.length === 0 && searchQuery.trim() !== '' && (
                                        <p style={{ fontSize: '0.875rem', color: '#555', marginTop: '0.5rem' }}>No se encontraron productos que coincidan con la búsqueda para la marca seleccionada.</p>
                                    )}
                                    {error && <p style={{ fontSize: '0.875rem', color: 'red', marginTop: '0.5rem' }}>{error}</p>} {/* Show search error here */}
                                </div>
                            )}

                            {actionType === 'add' && (
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', marginBottom: '0.75rem', paddingBottom: '0.5rem' }}>
                                        Ingresar Código de Producto
                                    </h3>
                                    {/* Display selected brand */}
                                    <p style={{ marginBottom: '1rem' }}>
                                        Marca Seleccionada: <strong>{brands.find(b => String(b.value) === String(selectedBrandId))?.label || 'N/A'}</strong>
                                    </p>

                                    {/* Use a simple div for input and button, not AntD Form here */}
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                                        <label htmlFor="productCodeCheck" className="editor-label" style={{ marginRight: '0.5rem', minWidth: '100px' }}>Código:</label>
                                        <Input
                                            id="productCodeCheck"
                                            placeholder="Ingrese el código del nuevo producto"
                                            value={formData.productCode}
                                            onChange={handleProductCodeChange}
                                            disabled={isCheckingCode || uniquenessCheckResult === 'exists' || isSubmitting || isFetchingProduct} // Disable while checking, if exists, or submitting/fetching
                                            style={{ flexGrow: 1, marginRight: '0.5rem' }}
                                        />
                                        <Button
                                            type="primary"
                                            onClick={handleCheckProductCode}
                                            loading={isCheckingCode}
                                            disabled={!formData.productCode.trim() || isCheckingCode || uniquenessCheckResult === 'exists' || isSubmitting || isFetchingProduct}
                                        >
                                            Verificar Código
                                        </Button>
                                    </div>

                                    {/* Uniqueness Check Results */}
                                    {isCheckingCode && <Spin tip="Verificando código..." style={{ marginTop: '0.5rem' }} />}
                                    {uniquenessCheckResult === 'exists' && (
                                        <div style={{ color: 'red', marginTop: '1rem' }}>
                                            <p>Ya existe un producto con este código para la marca seleccionada.</p>
                                            <p>¿Qué desea hacer?</p>
                                            <div style={{ marginTop: '1rem' }}>
                                                <Button type="primary" style={{ margin: '0 8px' }} onClick={handleGoToModifyExisting} disabled={isFetchingProduct || isSubmitting}>
                                                    Modificar Producto Existente (con este código)
                                                </Button>
                                                <Button type="default" style={{ margin: '0 8px' }} onClick={handleTryAnotherCode} disabled={isFetchingProduct || isSubmitting}>
                                                    Intentar con Otro Código
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                    {uniquenessCheckResult === 'unique' && (
                                        <p style={{ color: 'green', marginTop: '1rem' }}>Código de producto disponible. Puede continuar.</p>
                                    )}
                                    {error && <p style={{ fontSize: '0.875rem', color: 'red', marginTop: '0.5rem' }}>{error}</p>} {/* Show check error here */}
                                </div>
                            )}
                        </>
                    )}


                    {/* Step 4: Product Details (AntD Index 3) */}
                    {currentStep === 3 && (
                        <Spin spinning={isFetchingProduct} tip="Cargando detalles del producto..." size="large">
                            <div style={{ minHeight: '200px' }}>
                                {!isFetchingProduct && (
                                    <Form
                                        form={form}
                                        layout="vertical"
                                        onValuesChange={handleAntdFormChange}
                                        initialValues={formData}
                                        onFinish={handleSubmit}
                                    >
                                        <h3 style={{ fontSize: '1.5rem', paddingTop: '0rem', marginBottom: '1rem' }}>
                                            {mode === 'add' ? 'Detalles del Nuevo Producto' : `Editando Producto: ${selectedProductForEdit?.ProductCode || ''}`}
                                        </h3>

                                        {error && <p style={{ fontSize: '0.875rem', color: 'red', marginBottom: '1rem' }}>{error}</p>}

                                        {/* Brand (Read-only) */}
                                        <Form.Item label="Marca" name="brandId">
                                            <Input
                                                value={brands.find(b => String(b.value) === String(formData.brandId))?.label || ''}
                                                readOnly
                                                disabled
                                            />
                                        </Form.Item>

                                        {/* Product Code */}
                                        <Form.Item
                                            label="Código de Producto"
                                            name="productCode"
                                            rules={[{ required: true, message: 'Por favor, ingrese el código!' }]}
                                        >
                                            <Input
                                                disabled={mode === 'edit' || isCheckingCode || isSubmitting || isFetchingProduct}
                                            />
                                        </Form.Item>

                                        {/* Description */}
                                        <Form.Item
                                            label="Descripción"
                                            name="productDescription"
                                            rules={[{ required: true, message: 'Por favor, ingrese la descripción!' }]}
                                        >
                                            <Input />
                                        </Form.Item>

                                        {/* EAN field with the new button */}
                                        <Form.Item label="EAN" name="ean">
                                            <Input
                                                addonAfter={
                                                    <Button
                                                        onClick={handleGenerateEAN}
                                                        loading={isGeneratingEAN}
                                                        icon={<SyncOutlined />}
                                                        disabled={!formData.brandId || isGeneratingEAN}
                                                        type="text"
                                                        style={{ border: 'none', boxShadow: 'none' }}
                                                    />
                                                }
                                            />
                                        </Form.Item>

                                        {/* Cost */}
                                        <Form.Item
                                            label="Costo"
                                            name="productCost"
                                            rules={[{ required: true, message: 'Por favor, ingrese el costo!' }]}
                                        >
                                            <InputNumber
                                                min={0}
                                                step="0.01"
                                                style={{ width: '100%' }}
                                            />
                                        </Form.Item>

                                        {/* Quantity in Stock */}
                                        <Form.Item
                                            label="Cantidad en Stock"
                                            name="quantity"
                                            rules={[{ required: true, message: 'Por favor, ingrese la cantidad!' }]}
                                        >
                                            <InputNumber
                                                min={0}
                                                style={{ width: '100%' }}
                                            />
                                        </Form.Item>
                                    </Form>
                                )}
                            </div>
                        </Spin>
                    )}

                    {/* Message when in edit mode but no product is selected on step 4 */}
                    {/* This message should probably be on Step 2 (Modify Path) if no search results or nothing is selected after search */}
                    {/* Keeping it here for now, but might need relocation based on desired flow */}
                    {currentStep === 3 && actionType === 'modify' && !selectedProductForEdit && !isFetchingProduct && (
                        <div style={{ textAlign: 'center', color: '#666', marginTop: '2rem' }}>
                            <p>Seleccione un producto del paso anterior para editar.</p>
                        </div>
                    )}


                    {/* Step 5: Result (AntD Index 4) */}
                    {currentStep === 4 && submissionResult && (
                        <div style={{ textAlign: 'center', padding: '30px' }}>
                            <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: submissionResult.success ? '#0000FF' : 'red' }}>
                                {submissionResult.success ? (mode === 'add' ? 'Producto Agregado!' : 'Producto Actualizado!') : 'Error en la Operación'}
                            </h3>
                            <p style={{ marginBottom: '1.5rem', color: submissionResult.success ? 'white' : 'red' }}>
                                {submissionResult.message}
                            </p>



                            <div style={{ marginTop: '2rem' }}>
                                {submissionResult.success ? (
                                    <Button type="primary" size="large" onClick={handleStartNewProcess}>
                                        Editar Nuevo Producto
                                    </Button>
                                ) : (
                                    <>
                                        <Button type="primary" danger size="large" style={{ margin: '0 8px' }} onClick={handleRetry} disabled={isSubmitting || isCheckingCode || isFetchingProduct}>
                                            Reintentar ({mode === 'add' ? 'Agregar' : 'Guardar Cambios'})
                                        </Button>
                                        <Button type="default" size="large" style={{ margin: '0 8px' }} onClick={handleStartNewProcess} disabled={isSubmitting || isCheckingCode || isFetchingProduct}>
                                            Cancelar y Empezar de Nuevo
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    )}


                </div> {/* End of steps-content */}


                {/* --- Step Navigation Buttons --- */}
                <div className="steps-action" style={{ marginTop: '2rem', textAlign: 'center' }}>
                    {/* Previous Button */}
                    {currentStep > 0 && currentStep < steps.length - 1 && (
                        // Hide Previous button on the result step (Step 4) as we have dedicated buttons there
                        // Disable while loading/submitting/checking
                        <Button style={{ margin: '0 8px' }} onClick={() => prevStep()} disabled={isSubmitting || isCheckingCode || isFetchingProduct}>
                            Anterior
                        </Button>
                    )}

                    {/* Next Button (Only on Step 0 - Moves to Step 1) */}
                    {currentStep === 0 && (
                        <Button type="primary" onClick={() => {
                            if (isDemoMode) { message.info('Editor Manual — disponible en la versión completa del sistema'); return; }
                            nextStep();
                        }} disabled={!selectedBrandId || isSubmitting || loading || isCheckingCode || isFetchingProduct}>
                            Siguiente
                        </Button>
                    )}

                    {/* Navigation Buttons for Step 1 (Choose Action) are within the step content itself */}
                    {/* Navigation Buttons for Step 2 (Search/Validate) are within the step content itself */}

                    {/* Submit Button (Only on Step 3) */}
                    {currentStep === 3 && (
                        <Button
                            type="primary"
                            onClick={() => form.submit()} // Trigger AntD form submission
                            loading={isSubmitting || isCheckingCode || isFetchingProduct}
                            disabled={isSubmitting || isCheckingCode || isFetchingProduct}
                        >
                            {mode === 'add' ? 'Agregar Producto' : 'Guardar Cambios'}
                        </Button>
                    )}

                    {/* Buttons for Step 4 (Result) are within the step content itself */}

                </div>


            </div>{/* End of editor-content-container */}
        </div>
        </ConfigProvider>
    );
}

export default ProductEditor;